import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckCircle2,
  Clock,
  MapPin,
  Calendar,
  AlertCircle,
  X,
  Trash2,
  Edit,
  Check,
  Building2,
  User,
  Sparkles,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const OpsOverview = () => {
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [workloadForecast, setWorkloadForecast] = useState(null);
  const [forecastModalOpen, setForecastModalOpen] = useState(false);
  const [selectedForecastWeeks, setSelectedForecastWeeks] = useState(4);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
    fetchWorkloadForecast();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, teamRes, assignmentsRes] = await Promise.all([
        api.get('/ops/tasks'),
        api.get('/ops/team'),
        api.get('/ops/assignments')
      ]);
      
      setTasks(tasksRes.data || []);
      setTeamMembers(teamRes.data || []);
      setAssignments(assignmentsRes.data || []);
    } catch (error) {
      console.error('Error fetching ops data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkloadForecast = async (weeks = 4) => {
    try {
      const response = await api.get(`/ops/workload-forecast?weeks=${weeks}`);
      setWorkloadForecast(response.data);
    } catch (error) {
      console.error('Error fetching workload forecast:', error);
    }
  };

  const getWeekRange = () => {
    const start = new Date(currentWeekStart);
    start.setDate(start.getDate() - start.getDay() + 1);
    const end = new Date(start);
    end.setDate(start.getDate() + 4);
    
    return {
      start,
      end,
      formatted: `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    };
  };

  const previousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const getPriorityColor = (priority) => {
    switch(priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'medium':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'low':
        return 'bg-slate-50 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const getHourColorClass = (hours) => {
    if (hours < 5) return 'bg-slate-50 text-slate-600 border-slate-200';
    if (hours >= 5 && hours <= 7) return 'bg-slate-100 text-slate-700 border-slate-300';
    if (hours > 7 && hours <= 9) return 'bg-[#247C75]/10 text-[#247C75] border-[#247C75]/20';
    return 'bg-orange-50 text-orange-700 border-orange-200';
  };

  const getStatusBadge = (totalHours) => {
    if (totalHours >= 45) return { text: '↑ Over', className: 'bg-orange-50 text-orange-700 border-orange-200' };
    if (totalHours <= 30) return { text: '↓ Under', className: 'bg-[#247C75]/10 text-[#247C75] border-[#247C75]/20' };
    return { text: '✓ OK', className: 'bg-green-50 text-green-700 border-green-200' };
  };

  const getEventBorderColor = (type) => {
    if (type === 'confirmed') return 'border-l-green-500';
    if (type === 'tentative') return 'border-l-orange-500';
    if (type === 'conflict') return 'border-l-red-500';
    return 'border-l-[#247C75]';
  };

  const getAssignedMemberInitials = (task) => {
    // Get assigned team members for this task
    const taskAssignments = assignments.filter(a => a.project_id === task.id || a.task_id === task.id);
    if (taskAssignments.length === 0) return null;
    
    const members = taskAssignments.map(assignment => {
      const member = teamMembers.find(m => m.id === assignment.employee_id);
      if (member) {
        const initials = `${member.name?.split(' ')[0]?.[0] || ''}${member.name?.split(' ')[1]?.[0] || ''}`.toUpperCase();
        return { initials, name: member.name };
      }
      return null;
    }).filter(Boolean);
    
    return members;
  };

  const getDueDay = (date) => {
    if (!date) return null;
    const taskDate = new Date(date);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[taskDate.getDay()];
  };

  const openTaskModal = (task) => {
    setSelectedTask(task);
    setTaskModalOpen(true);
    // Generate AI recommendations when modal opens
    generateAIRecommendations(task);
  };

  const generateAIRecommendations = (task) => {
    if (!task || teamMembers.length === 0) {
      setAiRecommendations([]);
      return;
    }

    const recommendations = teamMembers.map(member => {
      let score = 0;
      const reasons = [];

      // Skill matching
      if (task.skills && member.skills) {
        const taskSkills = Array.isArray(task.skills) ? task.skills : [];
        const memberSkills = Array.isArray(member.skills) ? member.skills : [];
        const matchingSkills = taskSkills.filter(skill => 
          memberSkills.some(ms => ms.toLowerCase().includes(skill.toLowerCase()))
        );
        if (matchingSkills.length > 0) {
          score += matchingSkills.length * 30;
          reasons.push('Skill Match');
        }
      }

      // Capacity check (mock data - would check actual assignments)
      const currentLoad = Math.random() * 40; // Mock: 0-40 hours
      if (currentLoad < 35) {
        score += 25;
        reasons.push('Capacity OK');
      } else if (currentLoad >= 40) {
        score -= 20;
        reasons.push('Near Capacity');
      }

      // Availability on due date
      if (task.scheduled_date) {
        score += 15;
        reasons.push('Available on Due Date');
      }

      // Location proximity (if task and member have location)
      if (task.location && member.location && task.location === member.location) {
        score += 20;
        reasons.push('Nearby Project');
      }

      // Random base score for demo
      score += Math.floor(Math.random() * 10);

      return {
        member,
        score: Math.min(100, Math.max(0, score)),
        reasons: reasons.length > 0 ? reasons : ['General Match']
      };
    });

    // Sort by score and take top 3
    const topRecommendations = recommendations
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    setAiRecommendations(topRecommendations);
  };

  const assignSuggestedTeam = async () => {
    if (!selectedTask || aiRecommendations.length === 0) return;

    try {
      // Create assignments for top recommended members
      const topMember = aiRecommendations[0].member;
      
      // Create assignment
      await api.post('/ops/assignments', {
        task_id: selectedTask.id,
        employee_id: topMember.id,
        project_id: selectedTask.project_id || null,
        status: 'active'
      });

      toast.success(`Assigned ${topMember.name} to task`);
      fetchData();
    } catch (error) {
      console.error('Error assigning team:', error);
      toast.error('Failed to assign team member');
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeTask = tasks.find(t => t.id === active.id);
    const overColumn = over.id;

    if (!activeTask) return;

    // Determine new status based on column
    let newStatus = activeTask.status;
    if (overColumn === 'todo-column') newStatus = 'unassigned';
    else if (overColumn === 'progress-column') newStatus = 'in_progress';
    else if (overColumn === 'blocked-column') newStatus = 'blocked';

    // Update tasks optimistically
    if (activeTask.status !== newStatus) {
      setTasks(prevTasks => 
        prevTasks.map(t => 
          t.id === active.id ? { ...t, status: newStatus } : t
        )
      );
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeTask = tasks.find(t => t.id === active.id);
    if (!activeTask) return;

    // Determine new status based on drop zone
    let newStatus = activeTask.status;
    let statusLabel = '';
    
    if (over.id === 'todo-column') {
      newStatus = 'unassigned';
      statusLabel = 'To Do';
    } else if (over.id === 'progress-column') {
      newStatus = 'in_progress';
      statusLabel = 'In Progress';
    } else if (over.id === 'blocked-column') {
      newStatus = 'blocked';
      statusLabel = 'Blocked';
    }

    // Only update if status actually changed
    if (activeTask.status !== newStatus) {
      try {
        await api.put(`/ops/tasks/${active.id}`, { status: newStatus });
        toast.success(`Task moved to ${statusLabel}`);
        fetchData(); // Refresh data
      } catch (error) {
        console.error('Error updating task status:', error);
        toast.error('Failed to update task status');
        // Revert optimistic update
        fetchData();
      }
    }
  };

  const handleTaskUpdate = async (taskId, updates) => {
    try {
      await api.put(`/ops/tasks/${taskId}`, updates);
      toast.success('Task updated successfully');
      fetchData();
      setTaskModalOpen(false);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleTaskDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await api.delete(`/ops/tasks/${taskId}`);
      toast.success('Task deleted successfully');
      fetchData();
      setTaskModalOpen(false);
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#247C75]"></div>
      </div>
    );
  }

  const weekRange = getWeekRange();
  const todoTasks = tasks.filter(t => t.status === 'unassigned');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress' || t.status === 'assigned');
  const blockedTasks = tasks.filter(t => t.status === 'blocked');

  // Sortable Task Card Component
  const SortableTaskCard = ({ task, borderColor }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: task.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const members = getAssignedMemberInitials(task);
    const dueDay = getDueDay(task.scheduled_date);

    return (
      <div 
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={(e) => {
          if (!isDragging) {
            e.stopPropagation();
            openTaskModal(task);
          }
        }}
        className={`p-4 bg-white border ${borderColor} rounded-2xl hover:shadow-lg transition-all cursor-move ${
          isDragging ? 'shadow-2xl scale-105' : 'shadow-sm'
        }`}
      >
        <h4 className="text-sm font-semibold text-slate-900 mb-1 line-clamp-2">{task.title}</h4>
        {task.description && (
          <p className="text-xs text-slate-500 mb-3 line-clamp-1">{task.description}</p>
        )}
        {task.location && (
          <p className="text-xs text-slate-500 mb-3">{task.location}</p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {members && members.length > 0 && (
              <div className="flex -space-x-2">
                {members.slice(0, 3).map((member, idx) => (
                  <div 
                    key={idx}
                    className="h-7 w-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold border-2 border-white"
                    title={member.name}
                  >
                    {member.initials}
                  </div>
                ))}
                {members.length > 3 && (
                  <div className="h-7 w-7 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center text-xs font-semibold border-2 border-white">
                    +{members.length - 3}
                  </div>
                )}
              </div>
            )}
            {dueDay && task.department && (
              <div className="text-xs text-slate-500">
                Due {dueDay} • {task.department}
              </div>
            )}
          </div>
          <Badge 
            variant="outline" 
            className={`text-xs font-medium ${getPriorityColor(task.priority)}`}
          >
            {task.priority || 'medium'}
          </Badge>
        </div>
      </div>
    );
  };

  const activities = [
    { actor: 'Sarah Chen', action: 'assigned task to John Smith', time: '5 minutes ago' },
    { actor: 'Mike Johnson', action: 'completed "Deep Clean Suite 401"', time: '1 hour ago' },
    { actor: 'System', action: 'detected scheduling conflict for Team B', time: '2 hours ago' },
    { actor: 'Emily Davis', action: 'updated project timeline', time: '4 hours ago' },
    { actor: 'Admin', action: 'added new team member', time: 'Yesterday' }
  ];

  // Generate schedule events from real task data
  const getScheduleEvents = () => {
    const events = [];
    const weekStart = new Date(weekRange.start);
    const weekEnd = new Date(weekRange.end);
    
    // Filter tasks that have scheduled dates within the current week
    const scheduledTasks = tasks.filter(task => {
      if (!task.scheduled_date) return false;
      const taskDate = new Date(task.scheduled_date);
      return taskDate >= weekStart && taskDate <= weekEnd;
    });
    
    // Map tasks to schedule events
    scheduledTasks.forEach(task => {
      const taskDate = new Date(task.scheduled_date);
      const dayOfWeek = taskDate.getDay();
      // Convert Sunday (0) to 6, and shift Mon-Fri from 1-5 to 0-4
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      
      // Only show Monday-Friday (0-4)
      if (dayIndex >= 0 && dayIndex <= 4) {
        events.push({
          day: dayIndex,
          time: task.scheduled_start_time || '09:00',
          title: task.title,
          type: task.status === 'completed' ? 'confirmed' : 
                task.status === 'blocked' ? 'conflict' : 
                task.status === 'assigned' || task.status === 'in_progress' ? 'confirmed' : 'tentative',
          taskId: task.id
        });
      }
    });
    
    // Sort events by day and time
    events.sort((a, b) => {
      if (a.day !== b.day) return a.day - b.day;
      return a.time.localeCompare(b.time);
    });
    
    return events;
  };

  const scheduleEvents = getScheduleEvents();

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        {/* Top Row: Assignments and Schedule */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SECTION 1: Assignments with Drag and Drop */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900">Assignments</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-4">
                {/* To Do Column */}
                <div id="todo-column" className="min-h-[200px]">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">To Do</h3>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{todoTasks.length}</span>
                  </div>
                  <SortableContext items={todoTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3">
                      {todoTasks.slice(0, 2).map(task => (
                        <SortableTaskCard 
                          key={task.id} 
                          task={task} 
                          borderColor="border-slate-300 hover:border-slate-400"
                        />
                      ))}
                      {todoTasks.length === 0 && (
                        <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
                          <p className="text-xs">Drop tasks here</p>
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </div>

                {/* In Progress Column */}
                <div id="progress-column" className="min-h-[200px]">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">In Progress</h3>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{inProgressTasks.length}</span>
                  </div>
                  <SortableContext items={inProgressTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3">
                      {inProgressTasks.slice(0, 2).map(task => (
                        <SortableTaskCard 
                          key={task.id} 
                          task={task} 
                          borderColor="border-blue-300 hover:border-blue-400"
                        />
                      ))}
                      {inProgressTasks.length === 0 && (
                        <div className="text-center py-8 text-slate-400 border-2 border-dashed border-blue-200 rounded-2xl">
                          <p className="text-xs">Drop tasks here</p>
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </div>

                {/* Blocked Column */}
                <div id="blocked-column" className="min-h-[200px]">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Blocked</h3>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{blockedTasks.length}</span>
                  </div>
                  <SortableContext items={blockedTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3">
                      {blockedTasks.length > 0 ? (
                        blockedTasks.slice(0, 2).map(task => (
                          <SortableTaskCard 
                            key={task.id} 
                            task={task} 
                            borderColor="border-orange-300 hover:border-orange-400"
                          />
                        ))
                      ) : (
                        <div className="text-center py-8 text-slate-400 border-2 border-dashed border-orange-200 rounded-2xl">
                          <CheckCircle2 className="h-6 w-6 mx-auto mb-1" />
                          <p className="text-xs">No blocked tasks</p>
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </div>
              </div>
            </CardContent>
          </Card>

        {/* SECTION 2: Schedule */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Schedule (Week)</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={previousWeek} className="h-8 w-8 p-0">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-600">{weekRange.formatted}</span>
                <Button variant="ghost" size="sm" onClick={nextWeek} className="h-8 w-8 p-0">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day, dayIndex) => {
                const dayEvents = scheduleEvents.filter(e => e.day === dayIndex);
                return (
                  <div key={day}>
                    <div className="text-xs font-medium text-slate-600 mb-1.5">{day}</div>
                    {dayEvents.length > 0 ? (
                      <div className="space-y-1.5">
                        {dayEvents.map((event, idx) => (
                          <div key={idx} className={`flex items-center gap-3 px-3 py-2 bg-slate-50 border-l-2 ${getEventBorderColor(event.type)} rounded-r-md`}>
                            <Badge variant="outline" className="text-xs bg-white border-slate-200 text-slate-700">
                              {event.time}
                            </Badge>
                            <span className="text-sm text-slate-900">{event.title}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-2 text-xs text-slate-400">No events</div>
                    )}
                  </div>
                );
              })}
              <button className="w-full px-3 py-2.5 mt-1 border border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:border-[#247C75] hover:text-[#247C75] transition-colors flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" />
                Add Event
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Team Capacity and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SECTION 3: Team Capacity */}
        <Card className="lg:col-span-2 shadow-sm border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Team Capacity (This Week)</CardTitle>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border border-slate-200 bg-slate-50"></div>
                  <span className="text-slate-600">&lt;5h</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border border-slate-300 bg-slate-100"></div>
                  <span className="text-slate-600">5-7h</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border border-[#247C75]/20 bg-[#247C75]/10"></div>
                  <span className="text-slate-600">7-9h</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border border-orange-200 bg-orange-50"></div>
                  <span className="text-slate-600">&gt;9h</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2.5 px-3 font-medium text-slate-600">Team Member</th>
                    <th className="text-center py-2.5 px-3 font-medium text-slate-600">Mon</th>
                    <th className="text-center py-2.5 px-3 font-medium text-slate-600">Tue</th>
                    <th className="text-center py-2.5 px-3 font-medium text-slate-600">Wed</th>
                    <th className="text-center py-2.5 px-3 font-medium text-slate-600">Thu</th>
                    <th className="text-center py-2.5 px-3 font-medium text-slate-600">Fri</th>
                    <th className="text-center py-2.5 px-3 font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.slice(0, 5).map((member, idx) => {
                    const hoursPerDay = [
                      [7, 8, 6, 9, 7],
                      [5, 7, 8, 7, 10],
                      [4, 6, 7, 5, 6],
                      [8, 9, 8, 10, 9],
                      [6, 7, 6, 7, 8]
                    ][idx] || [7, 7, 7, 7, 7];
                    
                    const totalHours = hoursPerDay.reduce((a, b) => a + b, 0);
                    const status = getStatusBadge(totalHours);
                    
                    return (
                      <tr key={member.id} className="border-b border-slate-100">
                        <td className="py-2.5 px-3 font-medium text-slate-900">{member.name}</td>
                        {hoursPerDay.map((hours, dayIdx) => (
                          <td key={dayIdx} className="py-2.5 px-3 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${getHourColorClass(hours)}`}>
                              {hours}h
                            </span>
                          </td>
                        ))}
                        <td className="py-2.5 px-3 text-center">
                          <Badge variant="outline" className={`text-xs ${status.className}`}>
                            {status.text}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 4: Recent Activity */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {activities.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-slate-300 mt-1.5"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900">
                      <span className="font-medium">{activity.actor}</span>{' '}
                      <span className="text-slate-600">{activity.action}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Row: Workload Forecast */}
      {workloadForecast && (
        <div className="grid grid-cols-1 gap-6">
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900">AI Workload Forecast</CardTitle>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setForecastModalOpen(true)}
                  className="text-sm"
                >
                  View Details
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-4 mb-4">
                {[2, 4, 8].map(weeks => {
                  const forecast = workloadForecast.weeks === weeks ? workloadForecast : null;
                  const avgUtil = forecast?.avg_utilization || 0;
                  const status = avgUtil < 70 ? 'comfortable' : avgUtil < 90 ? 'tight' : 'overloaded';
                  
                  return (
                    <div 
                      key={weeks}
                      onClick={() => {
                        setSelectedForecastWeeks(weeks);
                        fetchWorkloadForecast(weeks);
                      }}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedForecastWeeks === weeks 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        {weeks} Weeks
                      </div>
                      {selectedForecastWeeks === weeks && workloadForecast ? (
                        <>
                          <div className="text-2xl font-bold text-slate-900 mb-1">
                            {Math.round(workloadForecast.avg_utilization)}%
                          </div>
                          <Badge className={`text-xs ${
                            status === 'comfortable' ? 'bg-green-500' :
                            status === 'tight' ? 'bg-amber-500' :
                            'bg-red-500'
                          } text-white`}>
                            {status === 'comfortable' ? '✓ Comfortable' :
                             status === 'tight' ? '⚠ Tight' :
                             '🔥 Overloaded'}
                          </Badge>
                        </>
                      ) : (
                        <div className="text-sm text-slate-500">Click to load</div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {workloadForecast && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-slate-900 mb-1">AI Insight</p>
                      <p className="text-sm text-slate-700">{workloadForecast.summary}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Forecast Detail Modal */}
      <Dialog open={forecastModalOpen} onOpenChange={setForecastModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-purple-600" />
              Workload Forecast - Next {selectedForecastWeeks} Weeks
            </DialogTitle>
          </DialogHeader>
          
          {workloadForecast && (
            <div className="space-y-6 mt-4">
              {/* Week-by-week breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Weekly Breakdown</h3>
                <div className="space-y-2">
                  {workloadForecast.forecasts.map((week, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="w-20 text-sm font-medium text-slate-900">
                        Week {week.week_number}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-600">{week.week}</span>
                          <span className="text-xs font-semibold text-slate-900">
                            {week.forecasted_hours}h / {week.team_capacity}h
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              week.status === 'comfortable' ? 'bg-green-500' :
                              week.status === 'tight' ? 'bg-amber-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, week.utilization_percent)}%` }}
                          ></div>
                        </div>
                      </div>
                      <Badge className={`text-xs ${
                        week.status === 'comfortable' ? 'bg-green-500' :
                        week.status === 'tight' ? 'bg-amber-500' :
                        'bg-red-500'
                      } text-white`}>
                        {week.utilization_percent}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  AI Analysis
                </h3>
                <p className="text-sm text-slate-700">{workloadForecast.summary}</p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-xs text-slate-600">Avg Utilization</div>
                    <div className="text-lg font-bold text-slate-900">{workloadForecast.avg_utilization}%</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-xs text-slate-600">Total Capacity</div>
                    <div className="text-lg font-bold text-slate-900">{workloadForecast.total_capacity}h</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTask && (
            <>
              <DialogHeader className="space-y-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <DialogTitle className="text-xl font-bold text-slate-900 mb-2">
                      {selectedTask.title}
                    </DialogTitle>
                    <div className="flex items-center gap-2">
                      <Badge 
                        className={`text-xs font-medium ${
                          selectedTask.priority === 'high' ? 'bg-orange-500 text-white' :
                          selectedTask.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {selectedTask.priority || 'medium'} priority
                      </Badge>
                      <Badge 
                        className={`text-xs font-medium ${
                          selectedTask.status === 'in_progress' || selectedTask.status === 'assigned' ? 'bg-blue-500 text-white' :
                          selectedTask.status === 'blocked' ? 'bg-orange-500 text-white' :
                          'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {selectedTask.status === 'in_progress' ? 'In Progress' : 
                         selectedTask.status === 'blocked' ? 'Blocked' : 
                         selectedTask.status === 'assigned' ? 'Assigned' : 'To Do'}
                      </Badge>
                    </div>
                  </div>
                  <button
                    onClick={() => setTaskModalOpen(false)}
                    className="text-slate-400 hover:text-slate-600 p-1"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-6">
                {/* Description */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Description</Label>
                  <p className="mt-1 text-sm text-slate-600">
                    {selectedTask.description || 'No description provided'}
                  </p>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Due Date
                    </Label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedTask.scheduled_date 
                        ? new Date(selectedTask.scheduled_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Estimated Hours
                    </Label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedTask.estimated_hours || 'N/A'}h
                    </p>
                  </div>
                </div>

                {/* Another Two Column Layout */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Department
                    </Label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedTask.department || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Created By
                    </Label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedTask.created_by || 'Tech Lead'}
                    </p>
                  </div>
                </div>

                {/* Assigned Team Members */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    <User className="h-4 w-4" />
                    Assigned Team Members
                  </Label>
                  <div className="flex items-center gap-3">
                    {getAssignedMemberInitials(selectedTask)?.map((member, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="h-10 w-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">
                          {member.initials}
                        </div>
                        <span className="text-sm text-slate-700">{member.name}</span>
                      </div>
                    ))}
                    {(!getAssignedMemberInitials(selectedTask) || getAssignedMemberInitials(selectedTask).length === 0) && (
                      <p className="text-sm text-slate-500">No team members assigned</p>
                    )}
                  </div>
                </div>

                {/* AI Recommendations */}
                {aiRecommendations.length > 0 && (
                  <div className="border border-slate-200 rounded-lg p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                    <Label className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      AI Recommendations
                    </Label>
                    <div className="space-y-3">
                      {aiRecommendations.map((rec, idx) => {
                        const initials = `${rec.member.name?.split(' ')[0]?.[0] || ''}${rec.member.name?.split(' ')[1]?.[0] || ''}`.toUpperCase();
                        return (
                          <div 
                            key={idx}
                            className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-200 hover:border-purple-300 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center text-sm font-semibold">
                                {initials}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{rec.member.name}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {rec.reasons.map((reason, ridx) => (
                                    <span 
                                      key={ridx}
                                      className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full"
                                    >
                                      {reason}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <Badge className={`text-xs font-semibold ${
                              rec.score >= 80 ? 'bg-green-500' :
                              rec.score >= 60 ? 'bg-blue-500' :
                              'bg-slate-500'
                            } text-white`}>
                              {rec.score}%
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                    <Button
                      onClick={assignSuggestedTeam}
                      className="w-full mt-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Assign Suggested Team
                    </Button>
                  </div>
                )}

                {/* Required Skills */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Required Skills</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.skills && selectedTask.skills.length > 0 ? (
                      selectedTask.skills.map((skill, idx) => (
                        <Badge 
                          key={idx}
                          variant="outline" 
                          className="text-xs font-medium border-slate-300 text-slate-700"
                        >
                          {skill}
                        </Badge>
                      ))
                    ) : (
                      <>
                        <Badge variant="outline" className="text-xs font-medium border-slate-300 text-slate-700">
                          DevOps
                        </Badge>
                        <Badge variant="outline" className="text-xs font-medium border-slate-300 text-slate-700">
                          Cloud Architecture
                        </Badge>
                        <Badge variant="outline" className="text-xs font-medium border-slate-300 text-slate-700">
                          Database Management
                        </Badge>
                      </>
                    )}
                  </div>
                </div>

                {/* Progress Update */}
                {selectedTask.progress_notes && (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Progress Update
                    </Label>
                    <p className="text-sm text-slate-600">{selectedTask.progress_notes}</p>
                  </div>
                )}

                {/* Change Status */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Change Status</Label>
                  <Select 
                    value={selectedTask.status} 
                    onValueChange={(value) => handleTaskUpdate(selectedTask.id, { status: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">To Do</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-slate-200 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => handleTaskDelete(selectedTask.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Task
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      // TODO: Implement edit functionality
                      toast.info('Edit functionality coming soon');
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => setTaskModalOpen(false)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Done
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </DndContext>
  );
};

export default OpsOverview;